import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const init = { first_name: "", last_name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState(init);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("bad");
      toast.success(t("contact.successToast"));
      setForm(init);
    } catch {
      toast.error(t("contact.errorToast"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="pill"><i className="fa-solid fa-paper-plane" /> {t("contact.pill")}</span>
          <h2 className="mt-5 font-display font-semibold text-4xl sm:text-5xl lg:text-[64px] leading-[1.0] tracking-tight max-w-[16ch]">
            {t("contact.titleLead")}
            <em className="italic font-light text-[hsl(var(--accent))]">{t("contact.titleEm")}</em>
            {t("contact.titleTail")}
          </h2>
          <p className="mt-6 text-[17px] leading-relaxed text-[hsl(var(--ink-soft))] max-w-md">{t("contact.paragraph")}</p>

          <div className="mt-10 space-y-5">
            <a href="tel:+41325100620" data-testid="contact-phone" className="flex items-center gap-4 group">
              <div className="w-11 h-11 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] flex items-center justify-center group-hover:bg-[hsl(var(--accent))] transition-colors">
                <i className="fa-solid fa-phone" />
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{t("contact.call")}</div>
                <div className="font-display font-medium">+41 32 510 06 20</div>
              </div>
            </a>
            <a href="mailto:hello@webprintmedia.swiss" data-testid="contact-email" className="flex items-center gap-4 group">
              <div className="w-11 h-11 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] flex items-center justify-center group-hover:bg-[hsl(var(--accent))] transition-colors">
                <i className="fa-solid fa-envelope" />
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{t("contact.email")}</div>
                <div className="font-display font-medium">hello@webprintmedia.swiss</div>
              </div>
            </a>
            <a href="https://wa.me/41793150600" target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 group">
              <div className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center">
                <i className="fa-brands fa-whatsapp text-lg" />
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{t("contact.whatsapp")}</div>
                <div className="font-display font-medium">{t("contact.whatsappValue")}</div>
              </div>
            </a>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 soft-card p-8 lg:p-10 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label={t("contact.firstName")} name="first_name" value={form.first_name} onChange={onChange} required />
            <Field label={t("contact.lastName")} name="last_name" value={form.last_name} onChange={onChange} required />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label={t("contact.emailField")} type="email" name="email" value={form.email} onChange={onChange} required />
            <Field label={t("contact.phone")} name="phone" value={form.phone} onChange={onChange} />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
              {t("contact.message")}
            </label>
            <textarea
              name="message"
              required
              rows={5}
              data-testid="contact-input-message"
              value={form.message}
              onChange={onChange}
              placeholder={t("contact.placeholder")}
              className="mt-2 w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-3 text-[15px] resize-none transition-colors"
            />
          </div>
          <div className="flex items-center justify-between pt-4 gap-3">
            <p className="text-[12px] text-[hsl(var(--muted-foreground))] max-w-xs">{t("contact.privacy")}</p>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] font-medium hover:bg-[hsl(var(--accent))] transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {loading ? t("contact.submitting") : t("contact.submit")}
              <i className="fa-solid fa-arrow-right" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
        {label}{required && <span className="text-[hsl(var(--accent))]"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`contact-input-${name}`}
        className="mt-2 w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-3 text-[15px] transition-colors"
      />
    </div>
  );
}
